import { useState, type ComponentType } from "react";
import { User, Users, Building, Landmark, MapPin, PhoneCall, Trophy, Map, Check, ArrowLeft, ArrowRight } from "lucide-react";
import { BOOKING_URL } from "@/components/site/Layout";
import {
  recommendPlan, planSlug, keyInclusions,
  type SizeAnswer, type LocationAnswer, type GoalAnswer,
} from "./plan-finder.logic";

type Option<T extends string> = { value: T; label: string; sub?: string; icon: ComponentType<{ className?: string }> };

const sizeOptions: Option<SizeAnswer>[] = [
  { value: "solo", label: "Just me", sub: "Solo attorney", icon: User },
  { value: "small", label: "2 to 5 attorneys", sub: "Small firm", icon: Users },
  { value: "mid", label: "6 to 25 attorneys", sub: "Mid-size firm", icon: Building },
  { value: "large", label: "25+ attorneys", sub: "Large or enterprise firm", icon: Landmark },
];
const locationOptions: Option<LocationAnswer>[] = [
  { value: "one", label: "1 location", icon: MapPin },
  { value: "two", label: "2 locations", icon: MapPin },
  { value: "three-four", label: "3 to 4 locations", icon: MapPin },
  { value: "five-plus", label: "5 or more locations", icon: MapPin },
];
const goalOptions: Option<GoalAnswer>[] = [
  { value: "calls", label: "More calls and consultations", sub: "Grow cases in my current market", icon: PhoneCall },
  { value: "competitors", label: "Beat my competitors", sub: "Outrank the firms above me", icon: Trophy },
  { value: "expand", label: "Expand to new cities", sub: "Rank in more locations", icon: Map },
];

const questions = [
  { title: "How many attorneys are at your firm?", options: sizeOptions },
  { title: "How many office locations do you want to rank?", options: locationOptions },
  { title: "What's your main goal for the next 12 months?", options: goalOptions },
] as const;

function track(event: string, params: Record<string, unknown> = {}) {
  if (typeof window === "undefined") return;
  const w = window as { gtag?: (...args: unknown[]) => void };
  w.gtag?.("event", event, { page_location: window.location.href, ...params });
}

export function SeoPlanFinder() {
  const [step, setStep] = useState(0); // 0..2 questions, 3 result
  const [answers, setAnswers] = useState<(string | null)[]>([null, null, null]);
  const [started, setStarted] = useState(false);

  const choose = (value: string) => {
    if (!started) { setStarted(true); track("plan_finder_start"); }
    setAnswers((a) => a.map((v, i) => (i === step ? value : v)));
  };

  const result = step === 3
    ? recommendPlan(answers[0] as SizeAnswer, answers[1] as LocationAnswer, answers[2] as GoalAnswer)
    : null;

  const next = () => {
    if (!answers[step]) return;
    if (step === 2) {
      const t = recommendPlan(answers[0] as SizeAnswer, answers[1] as LocationAnswer, answers[2] as GoalAnswer);
      track("plan_finder_result", { plan: planSlug(t) });
    }
    setStep((s) => s + 1);
  };

  const reset = () => { setAnswers([null, null, null]); setStep(0); };

  const chipLabels = answers.map((a, i) => questions[i].options.find((o) => o.value === a)?.label).filter(Boolean) as string[];

  return (
    <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 md:py-20">
      <section className="pf-root rounded-[28px] px-5 py-14 sm:px-10 md:py-20">
        <div className="mx-auto max-w-[720px] text-center">
          <p className="pf-gold text-xs font-bold uppercase tracking-[0.2em]">Find your plan · 30 seconds</p>
          <h2 className="mt-4 text-4xl md:text-5xl font-semibold pf-white">Which SEO plan fits your firm?</h2>
          <p className="mt-4 text-lg pf-soft">
            Answer three quick questions and we'll recommend the plan that fits your firm's size and goals.
          </p>
        </div>

        <div className="mx-auto mt-10 max-w-[720px]" aria-live="polite">
          {result ? (
            <div key="result" className="plan-finder-step pf-result rounded-[20px] p-6 sm:p-9 text-primary-foreground">
              <div className="flex flex-wrap items-center gap-2">
                {chipLabels.map((c) => (
                  <span key={c} className="rounded-full border border-primary-foreground/20 bg-primary-foreground/5 px-3 py-1 text-xs text-primary-foreground/80">{c}</span>
                ))}
              </div>
              <span className="mt-5 inline-block rounded-full bg-gold-gradient px-3 py-1 text-[10px] font-bold uppercase tracking-[0.16em] text-[var(--navy-deep)]">Recommended for you</span>
              <div className="mt-4 flex flex-wrap items-end justify-between gap-4">
                <h3 className="font-display text-[30px] font-semibold leading-tight text-primary-foreground">{result.name}</h3>
                <p className="text-primary-foreground/70">
                  <span className="text-5xl font-semibold text-[var(--gold)]">{result.price}</span> / month
                </p>
              </div>
              <p className="mt-3 text-primary-foreground/80">Built for: {result.best}.</p>
              <ul className="mt-6 grid gap-3 sm:grid-cols-2">
                {keyInclusions(result).map((f) => (
                  <li key={f} className="flex items-start gap-2.5 text-sm text-primary-foreground/90">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-[var(--success,#22c55e)]" aria-hidden />
                    {f}
                  </li>
                ))}
              </ul>
              <a
                href={`${BOOKING_URL}?plan=${planSlug(result)}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => track("plan_finder_book_call", { plan: planSlug(result) })}
                className="mt-8 flex w-full items-center justify-center gap-2 rounded-md bg-gold-gradient px-6 py-4 text-sm font-bold text-[var(--navy-deep)] shadow-[var(--shadow-card)] transition hover:-translate-y-0.5"
              >
                Book a call about this plan <ArrowRight className="h-4 w-4" aria-hidden />
              </a>
              <div className="mt-4 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs text-primary-foreground/70">
                <span>No setup fees</span>
                <a href="/pricing" className="underline underline-offset-4 hover:text-[var(--gold)]">Compare all plans</a>
                <button type="button" onClick={reset} className="underline underline-offset-4 hover:text-[var(--gold)]">Start over</button>
              </div>
            </div>
          ) : (
            <div key={step} className="plan-finder-step pf-card rounded-[20px] p-6 sm:p-9">
              <div className="grid grid-cols-3 gap-2" aria-hidden>
                {[0, 1, 2].map((i) => (
                  <span key={i} className={`h-1.5 rounded-full ${i <= step ? "pf-seg-on" : "pf-seg-off"}`} />
                ))}
              </div>
              <div className="mt-5 flex items-center justify-between text-xs font-semibold uppercase tracking-[0.14em] pf-muted">
                <span>Question {step + 1} of 3</span>
                {step > 0 ? (
                  <button type="button" onClick={() => setStep((s) => s - 1)} className="inline-flex items-center gap-1 normal-case tracking-normal text-sm pf-gold hover:opacity-80">
                    <ArrowLeft className="h-4 w-4" aria-hidden /> Back
                  </button>
                ) : null}
              </div>
              <h3 className="mt-3 font-display text-2xl font-semibold pf-white">{questions[step].title}</h3>
              <div className="mt-6 grid gap-3 sm:grid-cols-2">
                {questions[step].options.map((o) => {
                  const selected = answers[step] === o.value;
                  const Icon = o.icon;
                  return (
                    <button
                      key={o.value}
                      type="button"
                      aria-pressed={selected}
                      onClick={() => choose(o.value)}
                      className={`flex items-center gap-3 rounded-xl pf-opt p-4 text-left transition duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--gold)] ${selected ? "pf-opt-on" : ""}`}
                    >
                      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg pf-tile">
                        <Icon className="h-5 w-5" aria-hidden />
                      </span>
                      <span>
                        <span className="block font-semibold pf-white">{o.label}</span>
                        {o.sub ? <span className="block text-sm pf-muted">{o.sub}</span> : null}
                      </span>
                    </button>
                  );
                })}
              </div>
              <button
                type="button"
                onClick={next}
                disabled={!answers[step]}
                className="mt-7 inline-flex w-full items-center justify-center gap-2 pf-btn rounded-md px-6 py-3.5 text-sm font-bold transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40 sm:w-auto sm:float-right"
              >
                {step === 2 ? "See my plan" : "Next"} <ArrowRight className="h-4 w-4" aria-hidden />
              </button>
              <div className="clear-both" />
            </div>
          )}
        </div>
        <style>{`.pf-root{background:radial-gradient(1200px 400px at 80% -10%,#24406a 0%,transparent 60%),linear-gradient(160deg,#0f1b2d,#13233a)}.pf-gold{color:#f4c873}.pf-white{color:#fff}.pf-soft{color:#c9d2df}.pf-muted{color:#8e9bb0}.pf-card{background:rgba(255,255,255,0.04);border:1px solid rgba(255,255,255,0.10);box-shadow:0 20px 50px rgba(0,0,0,0.3)}.pf-seg-on{background:#e8a93a}.pf-seg-off{background:rgba(255,255,255,0.10)}.pf-opt{background:rgba(255,255,255,0.03);border:1.5px solid rgba(255,255,255,0.12)}.pf-opt:hover{border-color:rgba(232,169,58,0.6)}.pf-opt-on{border-color:#e8a93a;background:rgba(232,169,58,0.10);box-shadow:0 0 0 3px rgba(232,169,58,0.18)}.pf-tile{background:rgba(232,169,58,0.14);color:#f4c873}.pf-btn{background:linear-gradient(180deg,#f2bb55,#e8a93a);color:#0f1b2d}.pf-result{background:linear-gradient(160deg,#1b3150,#12223a);border:1px solid rgba(232,169,58,0.45);box-shadow:0 24px 60px rgba(0,0,0,0.45)}.plan-finder-step{animation:planFinderIn 200ms ease-out}@keyframes planFinderIn{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:none}}@media (prefers-reduced-motion:reduce){.pf-root{background:radial-gradient(1200px 400px at 80% -10%,#24406a 0%,transparent 60%),linear-gradient(160deg,#0f1b2d,#13233a)}.pf-gold{color:#f4c873}.pf-white{color:#fff}.pf-soft{color:#c9d2df}.pf-muted{color:#8e9bb0}.pf-card{background:rgba(255,255,255,0.04);border:1px solid rgba(255,255,255,0.10);box-shadow:0 20px 50px rgba(0,0,0,0.3)}.pf-seg-on{background:#e8a93a}.pf-seg-off{background:rgba(255,255,255,0.10)}.pf-opt{background:rgba(255,255,255,0.03);border:1.5px solid rgba(255,255,255,0.12)}.pf-opt:hover{border-color:rgba(232,169,58,0.6)}.pf-opt-on{border-color:#e8a93a;background:rgba(232,169,58,0.10);box-shadow:0 0 0 3px rgba(232,169,58,0.18)}.pf-tile{background:rgba(232,169,58,0.14);color:#f4c873}.pf-btn{background:linear-gradient(180deg,#f2bb55,#e8a93a);color:#0f1b2d}.pf-result{background:linear-gradient(160deg,#1b3150,#12223a);border:1px solid rgba(232,169,58,0.45);box-shadow:0 24px 60px rgba(0,0,0,0.45)}.plan-finder-step{animation:none}}`}</style>
      </section>
    </div>
  );
}
